import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../design-system/components/Button';
import { Card } from '../../design-system/components/Card';
import { TextField } from '../../design-system/components/TextField';
import { toApiError } from '../../shared/http/errors';
import { leddoStart } from './api';
import { useAuthStore } from './store';
import {
  isValidLeddoSubdomain,
  loadSavedWorkspace,
  normalizeLeddoSubdomain,
  saveWorkspace,
} from './leddoSubdomain';

const WORKSPACE_REQUIRED_MESSAGE =
  'Este email existe em mais de um workspace. Informe o workspace para continuar.';

export function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // leddo-universal v2 (Task 14): campo "Workspace" no login local só
  // aparece após o backend desambiguar email+senha ambíguo entre 2+
  // workspaces com `409 workspace_required` (nunca visível por padrão).
  const [workspaceRequired, setWorkspaceRequired] = useState(false);
  const [workspace, setWorkspace] = useState('');

  // Campo "Workspace Leddo" do bloco SSO: sempre visível, persiste em
  // localStorage e é validado no client antes de iniciar o OAuth.
  const [ssoWorkspace, setSsoWorkspace] = useState(() => loadSavedWorkspace());
  const [ssoWorkspaceError, setSsoWorkspaceError] = useState<string | null>(null);
  const [leddoLoading, setLeddoLoading] = useState(false);

  const login = useAuthStore((s) => s.login);
  const status = useAuthStore((s) => s.status);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLeddoLogin = async () => {
    const slug = normalizeLeddoSubdomain(ssoWorkspace);
    if (slug.length > 0 && !isValidLeddoSubdomain(slug)) {
      setSsoWorkspaceError('Use letras minúsculas, números e hífens.');
      return;
    }
    setSsoWorkspaceError(null);
    setLeddoLoading(true);
    try {
      if (slug.length > 0) saveWorkspace(slug);
      const { authorize_url } = await leddoStart(slug.length > 0 ? slug : undefined);
      window.location.assign(authorize_url);
    } catch (err) {
      setError(toApiError(err).message);
      setLeddoLoading(false);
    }
  };

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tenantSlug =
        workspaceRequired && workspace.trim().length > 0
          ? normalizeLeddoSubdomain(workspace)
          : null;
      await login({ email: email.trim(), password, tenantSlug });
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      const apiErr = toApiError(err);
      if (apiErr.code === 'workspace_required') {
        setWorkspaceRequired(true);
        return;
      }
      setError(apiErr.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-screen">
      <Card className="login-card">
        <h1 className="login-title">Leddo Copilot</h1>
        <p className="login-subtitle">Entre com suas credenciais.</p>
        <form className="login-form" onSubmit={handleSubmit}>
          <TextField
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <TextField
            label="Senha"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {workspaceRequired ? (
            <TextField
              label="Workspace"
              type="text"
              value={workspace}
              onChange={(e) => setWorkspace(e.target.value)}
              helperText={WORKSPACE_REQUIRED_MESSAGE}
              autoComplete="organization"
              required
              autoFocus
            />
          ) : null}
          {error !== null ? (
            <p className="login-error" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" isLoading={submitting} fullWidth>
            Entrar
          </Button>
        </form>
        <div className="login-divider" aria-hidden>
          <span>ou</span>
        </div>
        <div className="stack-sm">
          <TextField
            label="Workspace Leddo"
            type="text"
            placeholder="ex.: empresa-demo"
            value={ssoWorkspace}
            onChange={(e) => setSsoWorkspace(normalizeLeddoSubdomain(e.target.value))}
            {...(ssoWorkspaceError !== null ? { error: ssoWorkspaceError } : {})}
          />
          <Button
            variant="secondary"
            fullWidth
            isLoading={leddoLoading}
            onClick={() => void handleLeddoLogin()}
          >
            Entrar com Leddo
          </Button>
        </div>
      </Card>
    </main>
  );
}
